/**
 * Hook para gerenciamento de Atividades de Deals CRM
 * Fornece queries e mutations para histórico e atividades
 * 
 * Versão: 2.0.0
 * Atualização: Inclui anexos (crm_activity_attachments) na query
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import type { ActivityAttachment } from '@/hooks/useActivityAttachments';

// Logger para o módulo de Atividades
const logger = createLogger('DealActivities');

// Tipos de atividade
export type ActivityType = 
  | 'note'
  | 'call'
  | 'email'
  | 'meeting'
  | 'task'
  | 'phase_change'
  | 'value_change'
  | 'status_change'
  | 'assignment'
  | 'created'
  | 'product_added'
  | 'product_removed'
  | 'contract_generated'
  | 'charge_generated';

// Interface da Atividade
// RELEASE: 2026-01-10 v2.0.0 - deal_id agora opcional, adicionado customer_id
export interface DealActivity {
  id: string;
  tenant_id: string;
  deal_id: string | null;       // Agora opcional (pode ser null se vinculada apenas ao customer)
  customer_id: string | null;   // NOVO: ID do cliente vinculado
  activity_type: ActivityType;
  title: string;
  description: string | null;
  is_system: boolean;
  previous_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  scheduled_at: string | null;
  completed_at: string | null;
  performed_by: string | null;
  created_at: string;
  updated_at: string;
  // Relacionamento carregado - performer
  performer?: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  // Relacionamento carregado - anexos
  attachments?: ActivityAttachment[];
  // Contagem de comentários (mapeado da subquery)
  comments_count?: number;
  // RELEASE: 2026-02-15 v2.3.0 - Link de videoconferência do booking vinculado
  conferencing_link?: string | null;
}

// Input para criar atividade
// RELEASE: 2026-05-31 v3.0.0 - Passa a aceitar os 7 tipos suportados pela
// RPC create_deal_activity (whitelist: note|call|email|meeting|task|whatsapp|visit).
export interface CreateActivityInput {
  deal_id: string;
  activity_type: 'note' | 'call' | 'email' | 'meeting' | 'task' | 'whatsapp' | 'visit';
  title: string;
  description?: string;
  scheduled_at?: string | null;
  /** RELEASE: 2026-09-12 - IDs das pessoas mencionadas já na criação do andamento */
  mentioned_user_ids?: string[] | null;
}

// RELEASE: 2026-02-09 v2.2.0 - Interface para atualizar atividade
export interface UpdateActivityInput {
  activity_id: string;
  title?: string;
  description?: string | null;
  scheduled_at?: string | null;
}

// Opções de configuração do hook
export interface DealActivitiesOptions {
  enabled?: boolean;
}

/**
 * Hook para atividades de um deal específico
 * Suporta lazy loading via options.enabled
 */
export function useCrmDealActivities(dealId: string | null, options?: DealActivitiesOptions) {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();

  // Determina se query deve executar (lazy loading)
  const isEnabled = (options?.enabled ?? true) && !!dealId && !!currentTenant?.id;

  // Query para listar atividades do deal
  const activitiesQuery = useQuery({
    queryKey: ['crm-deal-activities', dealId],
    queryFn: async () => {
      if (!dealId || !currentTenant?.id) return [];

      const { data, error } = await supabase
        .from('crm_deal_activities')
        .select(`
          *,
          performer:profiles!crm_deal_activities_performed_by_fkey(id, full_name, avatar_url),
          attachments:crm_activity_attachments(
            id, file_name, file_type, file_size, public_url, thumbnail_url, mime_type
          ),
          comments:crm_activity_comments(count)
        `)
        .eq('deal_id', dealId)
        .eq('tenant_id', currentTenant.id)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Erro ao buscar atividades:', error.message);
        throw error;
      }

      // Mapeia contagem de comentários do formato Supabase [{count: N}] para campo direto
      const activities = (data || []).map((item: any) => ({
        ...item,
        comments_count: item.comments?.[0]?.count || 0,
        conferencing_link: null as string | null,
      })) as DealActivity[];

      // v3.0.0: conferencing_link via RPC get_booking_data_for_activities (eliminado SELECT direto a scheduling_bookings)
      if (activities.length > 0) {
        const activityIds = activities.map(a => a.id);
        const { data: bookingsData } = await supabase.rpc('get_booking_data_for_activities', {
          p_activity_ids: activityIds,
          p_tenant_hint: currentTenant.id,
        });

        if (bookingsData?.length) {
          const linkLookup: Record<string, string> = {};
          for (const b of bookingsData as any[]) {
            if (b.crm_activity_id && b.conferencing_link) {
              linkLookup[b.crm_activity_id] = b.conferencing_link;
            }
          }
          for (const act of activities) {
            act.conferencing_link = linkLookup[act.id] || null;
          }
        }
      }

      return activities;
    },
    enabled: isEnabled,
    staleTime: 30 * 1000, // Cache de 30 segundos
  });

  // Mutation para criar atividade manual
  const createActivity = useMutation({
    mutationFn: async (input: CreateActivityInput) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');

      // RELEASE: 2026-05-31 v3.0.0 - Passa a usar a RPC create_deal_activity
      // (ponto único). A RPC valida tenant ownership do deal, whitelist de tipo
      // e tamanhos de título/descrição.
      const { data: newActivityId, error: rpcError } = await supabase.rpc(
        'create_deal_activity',
        {
          p_deal_id: input.deal_id,
          p_activity_type: input.activity_type,
          p_title: input.title,
          p_description: input.description || null,
          // v2.1.1: Converte horário local para UTC antes de salvar
          p_scheduled_at: input.scheduled_at
            ? new Date(input.scheduled_at).toISOString()
            : null,
          p_performed_by: user?.id || null,
          p_source: 'manual',
          // RELEASE: 2026-09-12 - menções criadas de forma atômica pela RPC
          p_mentioned_user_ids: input.mentioned_user_ids?.length
            ? input.mentioned_user_ids
            : null,
          p_tenant_hint: currentTenant.id,
        } as never
      );

      if (rpcError) {
        logger.error('Erro ao criar atividade:', rpcError.message);
        throw rpcError;
      }

      // Hidrata o registro recém-criado para devolver o objeto completo
      const { data, error } = await supabase
        .from('crm_deal_activities')
        .select(`
          *,
          performer:profiles!crm_deal_activities_performed_by_fkey(id, full_name, avatar_url)
        `)
        .eq('id', newActivityId)
        .single();

      if (error) {
        logger.error('Erro ao hidratar atividade criada:', error.message);
        throw error;
      }
      return data as DealActivity;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities', dealId] });
      toast.success('Atividade registrada!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao registrar atividade: ${error.message}`);
    },
  });

  // Mutation para marcar atividade como concluída
  const completeActivity = useMutation({
    mutationFn: async (activityId: string) => {
      const { data, error } = await supabase
        .from('crm_deal_activities')
        .update({ completed_at: new Date().toISOString() })
        .eq('id', activityId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities', dealId] });
      toast.success('Atividade concluída!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao concluir atividade: ${error.message}`);
    },
  });

  // RELEASE: 2026-02-09 v2.1.0 - Mutation para reabrir atividade concluída
  const reopenActivity = useMutation({
    mutationFn: async (activityId: string) => {
      logger.debug('Reabrindo atividade:', activityId);
      
      const { data, error } = await supabase
        .from('crm_deal_activities')
        .update({ completed_at: null })
        .eq('id', activityId)
        .select()
        .single();

      if (error) {
        logger.error('Erro ao reabrir atividade:', error.message);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities', dealId] });
      toast.success('Atividade reaberta!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao reabrir atividade:', error.message);
      toast.error(`Erro ao reabrir atividade: ${error.message}`);
    },
  });

  // RELEASE: 2026-02-09 v2.2.0 - Mutation para atualizar atividade manual
  const updateActivity = useMutation({
    mutationFn: async (input: UpdateActivityInput) => {
      logger.debug('Atualizando atividade:', input.activity_id);
      
      // Monta objeto de updates apenas com campos fornecidos
      // Tipo seguro do Supabase para update de atividades
      const updates: Database['public']['Tables']['crm_deal_activities']['Update'] = {};
      
      if (input.title !== undefined) updates.title = input.title;
      if (input.description !== undefined) updates.description = input.description;
      if (input.scheduled_at !== undefined) {
        // v2.2.0: Converte horário local para UTC antes de salvar
        updates.scheduled_at = input.scheduled_at 
          ? new Date(input.scheduled_at).toISOString() 
          : null;
      }

      const { data, error } = await supabase
        .from('crm_deal_activities')
        .update(updates)
        .eq('id', input.activity_id)
        .eq('is_system', false) // Só edita atividades manuais
        .select()
        .single();

      if (error) {
        logger.error('Erro ao atualizar atividade:', error.message);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities', dealId] });
      toast.success('Atividade atualizada!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao atualizar atividade:', error.message);
      toast.error(`Erro ao atualizar atividade: ${error.message}`);
    },
  });

  // Mutation para deletar atividade (apenas manuais)
  const deleteActivity = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase
        .from('crm_deal_activities')
        .delete()
        .eq('id', activityId)
        .eq('is_system', false); // Só deleta manuais

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities', dealId] });
      toast.success('Atividade removida!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover atividade: ${error.message}`);
    },
  });

  return {
    activities: activitiesQuery.data || [],
    isLoading: activitiesQuery.isLoading,
    error: activitiesQuery.error,
    createActivity,
    updateActivity,
    completeActivity,
    reopenActivity,
    deleteActivity,
    refetch: activitiesQuery.refetch,
  };
}

/**
 * Mapeia tipo de atividade para ícone e cor
 */
export function getActivityConfig(type: ActivityType) {
  const configs: Record<ActivityType, { icon: string; color: string; label: string }> = {
    note: { icon: 'FileText', color: 'text-blue-500', label: 'Nota' },
    call: { icon: 'Phone', color: 'text-green-500', label: 'Ligação' },
    email: { icon: 'Mail', color: 'text-purple-500', label: 'E-mail' },
    meeting: { icon: 'Users', color: 'text-orange-500', label: 'Reunião' },
    task: { icon: 'CheckSquare', color: 'text-yellow-500', label: 'Lembrete' },
    phase_change: { icon: 'ArrowRight', color: 'text-muted-foreground', label: 'Mudança de Fase' },
    value_change: { icon: 'DollarSign', color: 'text-muted-foreground', label: 'Alteração de Valor' },
    status_change: { icon: 'Flag', color: 'text-muted-foreground', label: 'Mudança de Status' },
    assignment: { icon: 'UserPlus', color: 'text-muted-foreground', label: 'Atribuição' },
    created: { icon: 'Plus', color: 'text-muted-foreground', label: 'Criado' },
    product_added: { icon: 'Package', color: 'text-muted-foreground', label: 'Produto Adicionado' },
    product_removed: { icon: 'PackageMinus', color: 'text-muted-foreground', label: 'Produto Removido' },
    contract_generated: { icon: 'FileSignature', color: 'text-violet-500', label: 'Contrato Gerado' },
    charge_generated: { icon: 'CreditCard', color: 'text-emerald-500', label: 'Cobrança Gerada' },
  };

  return configs[type] || { icon: 'Circle', color: 'text-muted-foreground', label: type };
}
